/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

// cSpell:ignore shoppingcart, recaccount

import axios from 'axios';
import { SecurityHelper as SecurityUtils } from 'sarsaparilla';
import isEmpty from 'lodash/isEmpty';
import { gaSetRemoveFromCartEvent } from 'shared-ui/utils/gaCommon';
import { writeSession } from 'shared-ui/auth/session';

import * as accountTypes from 'ui-account/src/constants';
import { myPassesEndpoint } from 'ui-sitepass/src/api/endpoints';
import {
    getApplyPendingModificationsUrl,
    getDiscardPendingModificationsUrl,
    getDraftPriceSummaryUrl,
    getModifyReservationUrl,
    getMyReservationsURL,
    getOrderDetailsViewUrl,
    getQRCodesUrl,
} from '../api/endpoints';

export const GET_ORDER_DETAILS_REQUEST = 'GET_ORDER_DETAILS_REQUEST';
export const GET_ORDER_DETAILS_SUCCESS = 'GET_ORDER_DETAILS_SUCCESS';
export const GET_ORDER_DETAILS_ERROR = 'GET_ORDER_DETAILS_ERROR';
export const GET_DRAFT_PRICE_SUMMARY_REQUEST = 'GET_DRAFT_PRICE_SUMMARY_REQUEST';
export const GET_DRAFT_PRICE_SUMMARY_SUCCESS = 'GET_DRAFT_PRICE_SUMMARY_SUCCESS';
export const GET_DRAFT_PRICE_SUMMARY_ERROR = 'GET_DRAFT_PRICE_SUMMARY_ERROR';
export const GET_UPDATED_ORDER_SUMMARY_REQUEST = 'GET_UPDATED_ORDER_SUMMARY_REQUEST';
export const GET_UPDATED_ORDER_SUMMARY_SUCCESS = 'GET_UPDATED_ORDER_SUMMARY_SUCCESS';
export const GET_UPDATED_ORDER_SUMMARY_ERROR = 'GET_UPDATED_ORDER_SUMMARY_ERROR';
export const REMOVE_RESERVATION_FROM_CART_SUCCESS =
    'REMOVE_RESERVATION_FROM_CART_SUCCESS';
export const REMOVE_RESERVATION_FROM_CART_ERROR = 'REMOVE_RESERVATION_FROM_CART_ERROR';
export const OPEN_MODIFY_MODAL = 'OPEN_MODIFY_MODAL';
export const CLOSE_MODIFY_MODAL = 'CLOSE_MODIFY_MODAL';
export const CHANGE_TICKET_HOLDER_DETAILS = 'CHANGE_TICKET_HOLDER_DETAILS';
export const CHANGE_PASSES_DETAILS = 'CHANGE_PASSES_DETAILS';
export const CHANGE_PASSES_BY_GUEST_TYPE = 'CHANGE_PASSES_BY_GUEST_TYPE';
export const GET_TICKET_HOLDER_DETAILS_FROM_ACCOUNT =
    'GET_TICKET_HOLDER_DETAILS_FROM_ACCOUNT';
export const TOGGLE_NEED_TO_KNOW_CHECKBOX = 'TOGGLE_NEED_TO_KNOW_CHECKBOX';
export const TOGGLE_SITE_PASS_INFO_CHECKBOX = 'TOGGLE_SITE_PASS_INFO_CHECKBOX';
export const APPLY_PENDING_MODIFICATIONS_REQUEST = 'APPLY_PENDING_MODIFICATIONS_REQUEST';
export const APPLY_PENDING_MODIFICATIONS_SUCCESS = 'APPLY_PENDING_MODIFICATIONS_SUCCESS';
export const APPLY_PENDING_MODIFICATIONS_ERROR = 'APPLY_PENDING_MODIFICATIONS_ERROR';
export const SAVE_PENDING_MODIFICATIONS_REQUEST = 'SAVE_PENDING_MODIFICATIONS_REQUEST';
export const SAVE_PENDING_MODIFICATIONS_SUCCESS = 'SAVE_PENDING_MODIFICATIONS_SUCCESS';
export const SAVE_PENDING_MODIFICATIONS_ERROR = 'SAVE_PENDING_MODIFICATIONS_ERROR';
export const DISCARD_PENDING_MODIFICATIONS_REQUEST =
    'DISCARD_PENDING_MODIFICATIONS_REQUEST';
export const DISCARD_PENDING_MODIFICATIONS_SUCCESS =
    'DISCARD_PENDING_MODIFICATIONS_SUCCESS';
export const DISCARD_PENDING_MODIFICATIONS_ERROR = 'DISCARD_PENDING_MODIFICATIONS_ERROR';
export const RESET_ERROR = 'RESET_ERROR';
export const TOGGLE_SAVE_TO_ACCOUNT = 'TOGGLE_SAVE_TO_ACCOUNT';
export const RESET_SITE_PASS = 'RESET_SITE_PASS';
export const UPDATE_SITE_PASS_RESERVATIONS = 'UPDATE_SITE_PASS_RESERVATIONS';
export const UPDATE_SITE_PASS_REMOVED_RESERVATIONS_IDS =
    'UPDATE_SITE_PASS_REMOVED_RESERVATIONS_IDS';
export const UPDATE_SITE_PASS_TOTAL_PRICE = 'UPDATE_SITE_PASS_TOTAL_PRICE';
export const GET_QR_CODES_SUCCESS = 'GET_QR_CODES_SUCCESS';
export const GET_QR_CODES_ERROR = 'GET_QR_CODES_ERROR';
export const GET_MY_PASSES_REQUEST = 'GET_MY_PASSES';
export const GET_MY_PASSES_SUCCESS = 'GET_MY_PASSES_SUCCESS';
export const GET_MY_PASSES_ERROR = 'GET_MY_PASSES_ERROR';
export const GET_MY_RESERVATIONS_REQUEST = 'GET_MY_RESERVATIONS_REQUEST';
export const GET_MY_RESERVATIONS_SUCCESS = 'GET_MY_RESERVATIONS_SUCCESS';
export const GET_MY_RESERVATIONS_ERROR = 'GET_MY_RESERVATIONS_ERROR';
export const OPEN_PASS_MODAL = 'OPEN_PASS_MODAL';
export const CLOSE_PASS_MODAL = 'CLOSE_PASS_MODAL';

const normalizePassEntry = (pass) => {
    if (typeof pass === 'string') {
        return pass ? { type: pass } : null;
    }

    if (!pass || typeof pass !== 'object') {
        return null;
    }

    if (pass.is_digital) {
        return pass.number
            ? { is_digital: true, number: pass.number }
            : { is_digital: true };
    }

    return pass.type ? { type: pass.type } : null;
};

const normalizePassesByGuestTypeId = (passes = {}) =>
    Object.keys(passes).reduce((normalizedPasses, guestType) => {
        const entries = Array.isArray(passes[guestType]) ? passes[guestType] : [];
        const normalizedEntries = entries
            .map((entry) => normalizePassEntry(entry))
            .filter(Boolean);

        if (!isEmpty(normalizedEntries)) {
            normalizedPasses[guestType] = normalizedEntries;
        }

        return normalizedPasses;
    }, {});

const pickPassesForGuestTypes = (passesByGuestTypeId = {}, guestTypeIds = []) =>
    guestTypeIds.reduce((acc, guestType) => {
        if (passesByGuestTypeId[guestType]) {
            acc[guestType] = passesByGuestTypeId[guestType];
        }

        return acc;
    }, {});

export function toggleSaveToAccount(shouldSaveToAccount) {
    return (dispatch) => {
        dispatch({ type: TOGGLE_SAVE_TO_ACCOUNT, payload: shouldSaveToAccount });
    };
}

export function changeTicketHolderDetails(ticketHolderDetails, hasBeenModified = false) {
    return (dispatch) => {
        dispatch({
            type: CHANGE_TICKET_HOLDER_DETAILS,
            payload: { ticketHolderDetails, hasBeenModified },
        });
    };
}

export function toggleNeedToKnowCheckbox(value) {
    return {
        type: TOGGLE_NEED_TO_KNOW_CHECKBOX,
        payload: value,
    };
}

export function toggleSitePassInfoCheckbox(value) {
    return {
        type: TOGGLE_SITE_PASS_INFO_CHECKBOX,
        payload: value,
    };
}

export function updateTicketHolderDetails() {
    return (dispatch, getState) => {
        const { auth, timedEntry } = getState();
        const accountData = auth?.account?.account || {};
        const {
            orderDetails: { reservation, ticketHolderDetails },
        } = timedEntry;
        const ticketHolder = reservation?.ticket_holder;
        const vehicleDetails = reservation?.vehicle_details;

        const {
            first_name: firstName,
            last_name: lastName,
            email,
            home_address: homeAddress = {},
        } = accountData;
        const { country, state, zip_code: zipCode } = homeAddress;

        dispatch(
            changeTicketHolderDetails({
                firstName:
                    ticketHolderDetails.firstName || ticketHolder.first_name || firstName,
                lastName:
                    ticketHolderDetails.lastName || ticketHolder.last_name || lastName,
                email: ticketHolderDetails.email || ticketHolder.email || email,
                country:
                    ticketHolderDetails.country ||
                    ticketHolder.home_address.country ||
                    country,
                state:
                    ticketHolderDetails.state || ticketHolder.home_address.state || state,
                zipCode:
                    ticketHolderDetails.zipCode ||
                    ticketHolder.home_address.zip_code ||
                    zipCode,
                vehicleType:
                    ticketHolderDetails.vehicleType || vehicleDetails.vehicle_type,
                licensePlate:
                    ticketHolderDetails.licensePlate || vehicleDetails.license_number,
                totalNumberOfPeople:
                    ticketHolderDetails.totalNumberOfPeople ||
                    vehicleDetails.passenger_count,
            })
        );

        if (reservation.need_to_know_checked) {
            dispatch(toggleNeedToKnowCheckbox(true));
        }

        if (reservation.site_pass_info_checked) {
            dispatch(toggleSitePassInfoCheckbox(true));
        }
    };
}

export function saveChangesToAccount({
    firstName,
    lastName,
    email,
    country,
    state,
    zipCode,
}) {
    return (dispatch, getState) => {
        const { auth } = getState();
        const accountData = auth?.account?.account || {};

        const newProfile = {
            ...accountData,
            first_name: firstName,
            last_name: lastName,
            email,
            home_address: {
                ...accountData?.home_address,
                country,
                state,
                zip_code: zipCode,
            },
        };

        return axios
            .put(`${process.env.API}/accounts/account`, newProfile, {
                headers: { Authorization: SecurityUtils.getAuthHeader() },
            })
            .then((response) => {
                const profile = response?.data;

                if (response?.data && 'success' in response.data) {
                    dispatch({ type: accountTypes.UPDATE_EMAIL_SENT });
                } else {
                    writeSession(dispatch, profile);
                    dispatch({
                        type: accountTypes.ACCOUNT_UPDATED,
                    });
                }
            })
            .catch(() => {
                dispatch({
                    type: accountTypes.ACCOUNT_UPDATE_FAILED,
                });
            });
    };
}

export function clearError() {
    return (dispatch) => {
        dispatch({
            type: RESET_ERROR,
        });
    };
}

export function getOrderDetailsView(reservationId) {
    return (dispatch) => {
        dispatch({
            type: GET_ORDER_DETAILS_REQUEST,
        });
        return axios
            .get(getOrderDetailsViewUrl(reservationId), {
                headers: {
                    Authorization: SecurityUtils.getAuthHeader(),
                },
            })
            .then((response) => {
                dispatch({
                    type: GET_ORDER_DETAILS_SUCCESS,
                    payload: response.data,
                });
            })
            .catch((error) => {
                dispatch({
                    type: GET_ORDER_DETAILS_ERROR,
                    error,
                });

                throw error;
            });
    };
}

export function getMyPasses() {
    return async (dispatch) => {
        dispatch({
            type: GET_MY_PASSES_REQUEST,
        });
        try {
            const response = await axios.get(myPassesEndpoint(), {
                headers: {
                    Authorization: SecurityUtils.getAuthHeader(),
                },
            });
            dispatch({
                type: GET_MY_PASSES_SUCCESS,
                payload: response.data,
            });
        } catch (error) {
            dispatch({
                type: GET_MY_PASSES_ERROR,
                error,
            });

            throw error;
        }
    };
}

export function getMyReservations(selectedDate, facilityID) {
    return async (dispatch) => {
        dispatch({
            type: GET_MY_RESERVATIONS_REQUEST,
        });
        try {
            const response = await axios.get(
                `${getMyReservationsURL()}?tourDate=${selectedDate}&facilityID=${facilityID}&onlyConfirmedReservations=true`,
                {
                    headers: {
                        Authorization: SecurityUtils.getAuthHeader(),
                    },
                }
            );
            dispatch({
                type: GET_MY_RESERVATIONS_SUCCESS,
                payload: response.data,
            });
        } catch (error) {
            dispatch({
                type: GET_MY_RESERVATIONS_ERROR,
                error,
            });

            throw error;
        }
    };
}

export function removeFromCart(reservationId) {
    return (dispatch) => {
        return axios
            .delete(`${process.env.API}/cart/shoppingcart/${reservationId}`, {
                headers: {
                    Authorization: SecurityUtils.getAuthHeader(),
                },
            })
            .then(() => {
                gaSetRemoveFromCartEvent();
                dispatch({
                    type: REMOVE_RESERVATION_FROM_CART_SUCCESS,
                });
            })
            .catch((error) => {
                dispatch({
                    type: REMOVE_RESERVATION_FROM_CART_ERROR,
                    error,
                });

                throw error;
            });
    };
}

export function savePendingModifications(reservationId, pendingModification) {
    return (dispatch) => {
        dispatch({
            type: SAVE_PENDING_MODIFICATIONS_REQUEST,
        });
        return axios
            .post(getModifyReservationUrl(reservationId), pendingModification, {
                headers: {
                    Authorization: SecurityUtils.getAuthHeader(),
                },
            })
            .then((response) => {
                dispatch({
                    type: SAVE_PENDING_MODIFICATIONS_SUCCESS,
                    payload: response.data,
                });
            })
            .catch((error) => {
                dispatch({
                    type: SAVE_PENDING_MODIFICATIONS_ERROR,
                    error: error.error,
                });

                throw error;
            });
    };
}

export function updateWithTicketHolderDetails(reservationId, pendingMod = {}) {
    return (dispatch, getState) => {
        const { timedEntry } = getState();
        const { orderDetails } = timedEntry;
        const {
            reservation,
            ticketHolderDetails,
            passes,
            passes_by_guest_type_id,
            isNeedToKnowChecked,
            isSitePassInfoChecked,
        } = orderDetails;

        const updatedReservation = {
            ...reservation,
            ...pendingMod,
            need_to_know_checked: isNeedToKnowChecked,
            site_pass_info_checked: isSitePassInfoChecked,
            passes,
        };

        //We found that some reservations have this structure
        // "passes_by_guest_type_id": {
        // 	"10087438_3_day_personal_vehicle": []
        // }
        //We need to clean that structure first, if the user selects no guest type, then we send and empty object instead
        if (!isEmpty(passes_by_guest_type_id)) {
            delete updatedReservation.passes;
            // Keep a single pass-processing path for both pending and current mods:
            // pick the active guest types, then normalize to API-safe pass objects.
            const guestTypeIds = Object.keys(
                !isEmpty(pendingMod)
                    ? pendingMod.ticket_count_by_guest_type_id
                    : updatedReservation.ticket_count_by_guest_type_id
            );
            const scopedPasses = pickPassesForGuestTypes(
                passes_by_guest_type_id,
                guestTypeIds
            );
            updatedReservation.passes_by_guest_type_id =
                normalizePassesByGuestTypeId(scopedPasses);
        }

        updatedReservation.ticket_holder = {
            first_name: ticketHolderDetails.firstName,
            last_name: ticketHolderDetails.lastName,
            email: ticketHolderDetails.email,
            home_address: {
                state: ticketHolderDetails.state,
                country: ticketHolderDetails.country,
                zip_code: ticketHolderDetails.zipCode,
            },
        };

        updatedReservation.vehicle_details = {
            vehicle_type: ticketHolderDetails.vehicleType,
            license_number: ticketHolderDetails.licensePlate,
            passenger_count: ticketHolderDetails.totalNumberOfPeople,
        };

        updatedReservation.user_email = SecurityUtils.getAccount().email;

        dispatch({
            type: SAVE_PENDING_MODIFICATIONS_REQUEST,
        });

        return axios
            .post(getModifyReservationUrl(reservationId), updatedReservation, {
                headers: {
                    Authorization: SecurityUtils.getAuthHeader(),
                },
            })
            .then((response) => {
                dispatch({
                    type: SAVE_PENDING_MODIFICATIONS_SUCCESS,
                    payload: response.data,
                });
            })
            .catch((error) => {
                dispatch({
                    type: SAVE_PENDING_MODIFICATIONS_ERROR,
                    error,
                });

                throw error;
            });
    };
}

export function applyPendingModifications(reservationId) {
    return (dispatch) => {
        dispatch({
            type: APPLY_PENDING_MODIFICATIONS_REQUEST,
        });
        return axios
            .post(
                getApplyPendingModificationsUrl(reservationId),
                {},
                {
                    headers: {
                        Authorization: SecurityUtils.getAuthHeader(),
                    },
                }
            )
            .then(() => {
                dispatch({
                    type: APPLY_PENDING_MODIFICATIONS_SUCCESS,
                });
            })
            .catch((error) => {
                dispatch({
                    type: APPLY_PENDING_MODIFICATIONS_ERROR,
                    error,
                });
                throw error;
            });
    };
}

export function discardPendingModifications(reservationId) {
    return (dispatch) => {
        dispatch({
            type: DISCARD_PENDING_MODIFICATIONS_REQUEST,
        });
        return axios
            .post(
                getDiscardPendingModificationsUrl(reservationId),
                {},
                {
                    headers: {
                        Authorization: SecurityUtils.getAuthHeader(),
                    },
                }
            )
            .then(() => {
                dispatch({
                    type: DISCARD_PENDING_MODIFICATIONS_SUCCESS,
                });
            })
            .catch((error) => {
                dispatch({
                    type: DISCARD_PENDING_MODIFICATIONS_ERROR,
                    error,
                });
                throw error;
            });
    };
}

export function openModifyModal() {
    return {
        type: OPEN_MODIFY_MODAL,
    };
}

export function closeModifyModal() {
    return {
        type: CLOSE_MODIFY_MODAL,
    };
}

export function openWarningPassModal() {
    return {
        type: OPEN_PASS_MODAL,
    };
}

export function closeWarningPassModal() {
    return {
        type: CLOSE_PASS_MODAL,
    };
}

export function getDraftPriceSummary(passes) {
    return (dispatch, getState) => {
        const {
            timedEntry: {
                orderDetails: { reservation },
            },
        } = getState();

        const { reservation_id } = reservation;

        dispatch({
            type: GET_DRAFT_PRICE_SUMMARY_REQUEST,
        });
        return axios
            .post(
                getDraftPriceSummaryUrl(),
                {
                    reservation_id,
                    passes,
                },
                {
                    headers: {
                        Authorization: SecurityUtils.getAuthHeader(),
                    },
                }
            )
            .then((response) => {
                dispatch({
                    type: GET_DRAFT_PRICE_SUMMARY_SUCCESS,
                    payload: response.data,
                });
            })
            .catch((error) => {
                dispatch({
                    type: GET_DRAFT_PRICE_SUMMARY_ERROR,
                    error,
                });
            });
    };
}

export function getUpdatedOrderSummary(passes) {
    return (dispatch, getState) => {
        const {
            timedEntry: {
                orderDetails: { reservation },
            },
        } = getState();

        const { reservation_id } = reservation;
        const normalizedPasses = normalizePassesByGuestTypeId(passes);

        dispatch({
            type: GET_UPDATED_ORDER_SUMMARY_REQUEST,
        });
        return axios
            .post(
                getDraftPriceSummaryUrl(),
                {
                    reservation_id,
                    passes_by_guest_type_id: normalizedPasses,
                },
                {
                    headers: {
                        Authorization: SecurityUtils.getAuthHeader(),
                    },
                }
            )
            .then((response) => {
                dispatch({
                    type: GET_UPDATED_ORDER_SUMMARY_SUCCESS,
                    payload: response.data,
                });
            })
            .catch((error) => {
                dispatch({
                    type: GET_UPDATED_ORDER_SUMMARY_ERROR,
                    error,
                });
            });
    };
}

export function changePassesDetails(passes) {
    return (dispatch) => {
        dispatch({
            type: CHANGE_PASSES_DETAILS,
            payload: passes,
        });
    };
}

export function changePassesByGuestType(passes, hasBeenModified = false) {
    return (dispatch) => {
        dispatch({
            type: CHANGE_PASSES_BY_GUEST_TYPE,
            payload: { passes, hasBeenModified },
        });
    };
}

export function resetSitePass() {
    return (dispatch) => {
        dispatch({ type: RESET_SITE_PASS });
    };
}

export function updateSitePassReservations(reservations = []) {
    return (dispatch) => {
        dispatch({ type: UPDATE_SITE_PASS_RESERVATIONS, payload: reservations });
    };
}

export function updateSitePassRemovedReservationIds(removedReservationIds = []) {
    return (dispatch) => {
        dispatch({
            type: UPDATE_SITE_PASS_REMOVED_RESERVATIONS_IDS,
            payload: removedReservationIds,
        });
    };
}

export function updateSitePassTotalPrice(total = 0) {
    return (dispatch) => {
        dispatch({
            type: UPDATE_SITE_PASS_TOTAL_PRICE,
            payload: total,
        });
    };
}

export function getQRCodes(reservationId) {
    return (dispatch) => {
        return axios
            .get(getQRCodesUrl(reservationId), {
                headers: {
                    Authorization: SecurityUtils.getAuthHeader(),
                },
            })
            .then((response) => {
                dispatch({
                    type: GET_QR_CODES_SUCCESS,
                    payload: response.data,
                });
            })
            .catch((error) => {
                dispatch({
                    type: GET_QR_CODES_ERROR,
                    error,
                });
            });
    };
}
